import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import BurritoLogo from "@/components/BurritoLogo";
import RoleAvatar from "@/components/RoleAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  LayoutDashboard, 
  Package, 
  Thermometer, 
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Star,
  ShoppingCart,
  ClipboardCheck,
  Snowflake,
  Wind,
  Building2,
  Tag
} from "lucide-react";
import { toast } from "sonner";

const navItems = [
  { path: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { path: "/products", labelKey: "nav.products", icon: Package },
  { path: "/favorites", labelKey: "nav.favorites", icon: Star },
  { path: "/temperature", labelKey: "nav.temperature", icon: Thermometer },
  { path: "/refrigerators", labelKey: "nav.refrigerators", icon: Snowflake },
  { path: "/cooling-cell", labelKey: "nav.cooling", icon: Wind },
  { path: "/suppliers", labelKey: "nav.suppliers", icon: Building2 },
  { path: "/labels", labelKey: "nav.labels", icon: Tag },
  { path: "/closing-checklist", labelKey: "nav.closing", icon: ClipboardCheck },
  { path: "/purchase-list", labelKey: "nav.purchaseList", icon: ShoppingCart },
  { path: "/reports", labelKey: "nav.reports", icon: FileText },
  { path: "/settings", labelKey: "nav.settings", icon: Settings, adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t("login.loggedOut"));
      navigate("/");
    } catch (error) {
      toast.error("Failed to logout");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="app-header sticky top-0 z-30 border-b border-border/80 backdrop-blur-xl">
        <div className="flex items-center justify-between h-16 px-4 lg:px-7">
          {/* Logo & Mobile Menu */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 rounded-md hover:bg-zinc-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2.5 group">
              <span className="w-9 h-9 rounded-xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center shadow-sm group-hover:shadow transition-shadow">
                <BurritoLogo className="w-8 h-8" />
              </span>
              <span className="font-heading font-semibold text-lg text-foreground hidden sm:block tracking-tight">
                Tracko Memo
              </span>
            </Link>
            <span className="hidden lg:block h-5 w-px bg-border mx-1" aria-hidden="true" />
            <span className="hidden lg:block text-xs text-muted-foreground">Centro de operaciones</span>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <div className="flex items-center border border-border/80 rounded-lg overflow-hidden text-xs bg-muted/40 p-0.5">
              <button
                type="button"
                className={`px-2.5 py-1.5 transition-colors ${
                  lang === "es" ? "bg-primary text-white rounded-md shadow-sm" : "text-muted-foreground hover:bg-white rounded-md"
                }`}
                onClick={() => setLang("es")}
                data-testid="lang-es-btn"
              >
                ES
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 transition-colors ${
                  lang === "fr" ? "bg-primary text-white rounded-md shadow-sm" : "text-muted-foreground hover:bg-white rounded-md"
                }`}
                onClick={() => setLang("fr")}
                data-testid="lang-fr-btn"
              >
                FR
              </button>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-10 px-2 rounded-xl hover:bg-primary/5" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 ring-1 ring-primary/10 flex items-center justify-center overflow-hidden">
                    <RoleAvatar role={user?.role} name={user?.name} email={user?.email} className="w-8 h-8" />
                  </div>
                  <div className="hidden md:flex flex-col items-start">
                    <span className="text-sm font-medium leading-tight">{user?.name}</span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
                      {user?.role === "admin" ? t("role.admin") : t("role.user")}
                    </span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="h-4 w-4 mr-2" />
                  {t("header.settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("header.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="app-sidebar hidden lg:flex flex-col w-60 min-h-[calc(100vh-4rem)] text-primary-foreground sticky top-16 self-start">
          <div className="px-5 pt-6 pb-3 text-[10px] font-medium uppercase tracking-[0.18em] text-white/45">
            Navegación
          </div>
          <nav className="flex-1 px-3 pb-5 space-y-1">
            {navItems
              .filter((item) => !item.adminOnly || user?.role === "admin")
              .map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                      isActive
                        ? "bg-white/15 text-white font-medium shadow-sm ring-1 ring-white/10"
                        : "text-white/65 hover:bg-white/10 hover:text-white"
                    }`}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon className="h-[17px] w-[17px] flex-shrink-0" />
                    {t(item.labelKey)}
                  </Link>
                );
              })}
          </nav>
          <div className="m-3 mt-0 rounded-xl bg-white/10 ring-1 ring-white/10 px-3 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/90 flex items-center justify-center overflow-hidden flex-shrink-0">
              <RoleAvatar role={user?.role} name={user?.name} email={user?.email} className="w-9 h-9" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/55 mt-0.5">
                {user?.role === "admin" ? t("role.admin") : t("role.user")}
              </p>
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="fixed inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
            <aside className="app-sidebar fixed left-0 top-0 bottom-0 w-64 text-white shadow-2xl animate-fade-in">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                  <BurritoLogo className="w-8 h-8" />
                  <span className="font-heading font-semibold text-lg tracking-tight">Tracko Memo</span>
                </Link>
                <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="p-3 space-y-1">
                {navItems
                  .filter((item) => !item.adminOnly || user?.role === "admin")
                  .map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors ${
                        isActive
                          ? "bg-white/15 text-white font-medium ring-1 ring-white/10"
                          : "text-white/65 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {t(item.labelKey)}
                    </Link>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="app-main-surface flex-1 p-4 sm:p-6 lg:p-8 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}
